import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../App";
import {
  Zap, Trophy, Target, Flame, TrendingUp,
  Star, Shield, ArrowLeft
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { ShareButton } from "../components/ShareModal";

export default function Rewards() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/rewards-v2/dashboard").then(res => {
      setDashboard(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = dashboard;
  const bbs = d?.bitcoin_brain_score;
  const capPct = ((d?.total_lifetime_xp || 0) / 21000000 * 100);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="rewards-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Dashboard</span>
          </button>
          <h1 className="font-heading text-xl font-bold text-white">REWARDS</h1>
          <div className="flex items-center gap-2 bg-[#F7931A]/10 px-3 py-1.5 rounded-lg border border-[#F7931A]/30">
            <Zap className="text-[#F7931A]" size={16} />
            <span className="font-mono text-[#F7931A] text-sm" data-testid="total-xp">{(d?.total_lifetime_xp || 0).toLocaleString()} BTC XP</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Bitcoin Brain Score */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="card-dark p-6" data-testid="brain-score-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
              <Target className="text-[#F7931A]" size={24} />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-white">BITCOIN BRAIN SCORE</h2>
              <p className="text-zinc-500 text-sm">{bbs?.label}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="font-mono text-4xl font-bold gradient-text">{bbs?.score || 0}</p>
              <ShareButton
                text={`My Bitcoin Brain Score is ${bbs?.score || 0} — "${bbs?.label}." How much do you know about Bitcoin?`}
                url={`${window.location.origin}/demo`}
                title="Bitcoin Brain Score"
                className="mt-1 justify-end"
              />
            </div>
          </div>
          <Progress value={Math.min(100, (bbs?.score || 0) / 210)} className="h-3 mb-4" />
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {bbs && Object.entries(bbs.breakdown).map(([key, val]) => (
              <div key={key} className="text-center p-2 bg-[#0A0A0A] rounded-lg">
                <p className="text-[#F7931A] font-mono text-lg font-bold">{val}</p>
                <p className="text-zinc-500 text-xs capitalize">{key.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* BTC XP Daily Tracker */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="card-dark p-6" data-testid="daily-xp-card">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="text-[#F7931A]" size={20} />
              <span className="text-white font-medium">Today's BTC XP</span>
            </div>
            <div className="text-center mb-4">
              <p className="font-mono text-4xl font-bold text-[#F7931A]">+{(d?.xp_earned_today || 0).toLocaleString()}</p>
              <p className="text-zinc-500 text-sm">BTC XP earned today</p>
            </div>

            {/* 21M Cap */}
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-zinc-500">21M Lifetime Cap</span>
                <span className="text-[#F7931A] font-mono">{capPct.toFixed(4)}%</span>
              </div>
              <div className="w-full h-2 bg-[#161616] rounded-full overflow-hidden border border-white/5">
                <div className="h-full bg-gradient-to-r from-[#F7931A] to-[#F7931A]/60 rounded-full" style={{ width: `${Math.max(0.5, capPct)}%` }} />
              </div>
            </div>

            <p className="text-zinc-600 text-[10px] mt-3 text-center italic">Resets at midnight UTC daily — lifetime BTC XP capped at 21M</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="card-dark p-6" data-testid="xp-level-card">
            <div className="flex items-center gap-2 mb-4">
              <Star className="text-[#F7931A]" size={20} />
              <span className="text-white font-medium">BTC XP Level & Title</span>
            </div>
            <div className="text-center mb-4">
              <p className="font-mono text-3xl font-bold text-[#F7931A]">Level {d?.xp_level || 1}</p>
              <p className="text-white text-lg">{d?.xp_title || "Fiat Sleeper"}</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Base BTC XP</span>
                <span className="font-mono text-white">{(d?.total_lifetime_xp || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Lifetime BTC XP</span>
                <span className="font-mono text-[#F7931A]">{(d?.total_lifetime_xp || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Today</span>
                <span className="font-mono text-white">+{(d?.xp_earned_today || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">Streak</span>
                <span className="flex items-center gap-1 text-orange-400 font-mono">
                  <Flame size={14} /> {d?.streak_days || 0} days
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Badges earned</span>
                <span className="font-mono text-white">{d?.badges_count || 0}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* BTC XP Store Banner */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-gradient-to-r from-[#F7931A]/10 to-[#00E5FF]/5 border border-[#F7931A]/20 rounded-xl p-5 cursor-pointer hover:border-[#F7931A]/40 transition-colors"
          onClick={() => navigate("/xp-store")}
          data-testid="xp-store-banner"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-bold text-lg mb-1">BTC XP Store</h3>
              <p className="text-zinc-400 text-sm">Spend BTC XP on streak shields, boosts, quiz power-ups, and exclusive cosmetics</p>
            </div>
            <div className="text-[#F7931A] font-heading text-2xl">→</div>
          </div>
        </motion.div>

        {/* BTC XP Earning Guide */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="card-dark p-6" data-testid="xp-guide">
          <h3 className="text-white font-medium mb-4">BTC XP Earning Guide</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { action: "Read section", xp: 500 },
              { action: "Quiz 3/3", xp: 700 },
              { action: "Quiz 2/3", xp: 300 },
              { action: "Quiz game level", xp: 400 },
              { action: "Vocab card", xp: "50-200" },
              { action: "Sat math", xp: "100-250" },
              { action: "Rabbit hole", xp: 300 },
              { action: "Weekly quiz", xp: 500 }
            ].map(item => (
              <div key={item.action} className="p-3 bg-[#0A0A0A] rounded-lg text-center">
                <p className="text-[#F7931A] font-mono font-bold">+{item.xp}</p>
                <p className="text-zinc-500 text-xs mt-1">{item.action}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Quick Nav */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Achievements", icon: Trophy, path: "/achievements", color: "text-[#F7931A]" },
            { label: "Leaderboard", icon: TrendingUp, path: "/leaderboard", color: "text-green-400" },
            { label: "Badges", icon: Shield, path: "/achievements", color: "text-purple-400" },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="p-4 card-dark flex flex-col items-center gap-2 hover:border-[#F7931A]/50 transition-all"
              data-testid={`nav-${item.label.toLowerCase()}-btn`}
            >
              <item.icon className={item.color} size={24} />
              <span className="text-white text-sm">{item.label}</span>
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
